import { useMemo } from "react";
import { CompositionDto } from "@repo/api/editor";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  AnimatedImageForTimings,
  getCompositionTimings,
  getVoiceOverStartFrame,
  msToFrames,
} from "@repo/video-engine-i2v/utils/timing";

// Ignore small overflows, which are usually trailing silence in the audio
const MIN_OVERFLOW_MS = 500;

/**
 * How many milliseconds of the voiceover play past the end of the video and get
 * cut off, or 0 if it fits.
 */
export function getVoiceOverOverflowMs(
  composition: CompositionDto,
  animatedImages: Record<string, AnimatedImageForTimings>,
  durationMs: number,
) {
  const timings = getCompositionTimings(composition, animatedImages);
  const endFrame =
    getVoiceOverStartFrame(timings, composition.voiceOver, timings.fps) +
    msToFrames(durationMs, timings.fps);
  const overflowMs =
    ((endFrame - timings.durationInFrames) / timings.fps) * 1000;

  return overflowMs >= MIN_OVERFLOW_MS ? overflowMs : 0;
}

export function getVoiceOverOverflowMessage(overflowMs: number) {
  return `The voiceover is ${(overflowMs / 1000).toFixed(1)}s longer than the video and will be cut off.`;
}

export function getVoiceOverOverflowHint(overflowMs: number) {
  return `${(overflowMs / 1000).toFixed(1)}s longer than the video and will be cut off.`;
}

export function getVoiceOverSilenceHint(overflowMs: number) {
  return `The voiceover is ${(overflowMs / 1000).toFixed(1)}s too long. Reduce this silence to give it more room.`;
}

export function useVoiceOverOverflowMs(durationMs: number | undefined) {
  const composition = useEditorStore((state) => state.video?.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);

  return useMemo(() => {
    if (!composition || durationMs === undefined) return 0;

    return getVoiceOverOverflowMs(composition, animatedImages, durationMs);
  }, [composition, animatedImages, durationMs]);
}
